import React, { type ReactNode } from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { modalHeaderContract } from './modal-header.contract'

export const MODAL_HEADER_TITLE = variantForStyleId(modalHeaderContract.textStyleIds[0])

export type ModalHeaderProps = {
    title: string
    /**
     * Figma's `slot`, which ships a `Close` glyph as its example content. The
     * component takes whatever the caller puts there rather than baking the
     * close button in: a slot is an opening in the API, not a default.
     */
    slot?: ReactNode
    className?: string
    testID?: string
}

/**
 * The top of a modal: a title that takes the width, and a slot at the trailing
 * edge.
 *
 * Only the top corners are rounded and only the bottom edge is stroked, which
 * is what lets a header, a body and a footer sit as three components and read
 * as one card. Both are measured off the node — `topLeftRadius`/`topRightRadius`
 * bind `cards` and nothing binds the bottom pair, `strokeBottomWeight` is 1 and
 * the other three sides are 0.
 */
export const ModalHeader = ({ title, slot, className, testID }: ModalHeaderProps) => (
    <View
        className={twMerge(
            'flex-row items-center gap-2xl rounded-t-cards border-b bg-background-sub-surface px-2xl py-l border-b-border-subtle',
            className,
        )}
        testID={testID ?? 'modal-header'}
    >
        <Typography
            variant={MODAL_HEADER_TITLE}
            tone='default'
            className='flex-1'
            testID='modal-header-title'
        >
            {title}
        </Typography>
        {slot ? (
            <View className='flex-row items-center gap-s' testID='modal-header-slot'>
                {slot}
            </View>
        ) : null}
    </View>
)

ModalHeader.displayName = 'ModalHeader'
